import type { ReactNode } from 'react';
import { cloneElement, forwardRef, isValidElement } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { Button } from '@anyroad/button';
import { usePopoverContext } from './context';

type PopoverTriggerProps = {
  /** Allows the user to pass any element as the trigger. */
  asChild?: boolean;
  /** The children of the popover trigger. */
  children: ReactNode;
};

/**
 * Popover trigger component.
 *
 * @type {React.FC<PopoverTriggerProps>}
 * @returns {React.ReactElement} The popover trigger.
 * @example
 * import { PopoverTrigger } from './Popover';
 */
export const PopoverTrigger = forwardRef<HTMLElement, PopoverTriggerProps>(
  ({ children, asChild = false, ...props }, propRef) => {
    const { refs, getReferenceProps, open } = usePopoverContext();
    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([refs.setReference, propRef, childrenRef]);

    if (asChild && isValidElement(children)) {
      return cloneElement(
        children,
        getReferenceProps({
          ref,
          ...props,
          ...children.props,
          'data-state': open ? 'open' : 'closed',
        })
      );
    }

    return (
      <Button
        data-state={open ? 'open' : 'closed'}
        ref={ref}
        {...getReferenceProps(props)}
      >
        {children}
      </Button>
    );
  }
);

PopoverTrigger.displayName = 'PopoverTrigger';
